import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Plus, Printer, FileText, Truck, Trash2, Eye, ClipboardList, Receipt,
  Lock, X, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const FRONTEND_URL = (typeof window !== 'undefined' ? window.location.origin : '');

const ShippingPage = () => {
  const { api } = useAuth();
  const [tab, setTab] = useState('picklists');
  const [loading, setLoading] = useState(false);
  const [picklists, setPicklists] = useState([]);
  const [bols, setBols] = useState([]);
  const [units, setUnits] = useState([]);
  const [boms, setBoms] = useState([]);
  const [search, setSearch] = useState('');

  // Picklist BOM expand
  const [bomToAdd, setBomToAdd] = useState('');

  // Picklist dialog
  const [plDialogOpen, setPlDialogOpen] = useState(false);
  const [plEditing, setPlEditing] = useState(null);
  const [plForm, setPlForm] = useState(blankPicklist());

  // BoL dialog
  const [bolDialogOpen, setBolDialogOpen] = useState(false);
  const [bolEditing, setBolEditing] = useState(null);
  const [bolForm, setBolForm] = useState(blankBol());

  // Detail view
  const [viewOpen, setViewOpen] = useState(false);
  const [viewKind, setViewKind] = useState(null); // 'pl' | 'bol'
  const [viewDoc, setViewDoc] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [plRes, bolRes, fuRes, bomRes] = await Promise.all([
        api().get('/picklists'),
        api().get('/bills-of-lading'),
        api().get('/finished-units'),
        api().get('/boms?status=Released').catch(() => ({ data: [] })),
      ]);
      setPicklists(plRes.data);
      setBols(bolRes.data);
      setUnits(fuRes.data);
      setBoms(bomRes.data || []);
    } catch (e) {
      toast.error('Failed to load shipping data');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const unitMap = useMemo(() => units.reduce((acc, u) => { acc[u.id] = u; return acc; }, {}), [units]);

  // ===== Picklist handlers =====
  const openPlCreate = () => { setPlEditing(null); setPlForm(blankPicklist()); setPlDialogOpen(true); };
  const openPlEdit = (pl) => {
    setPlEditing(pl);
    setPlForm({
      finished_unit_id: pl.finished_unit_id || '',
      title: pl.title || '',
      customer: pl.customer || '',
      items: pl.items || [],
      notes: pl.notes || '',
    });
    setPlDialogOpen(true);
  };
  const fillFromUnit = async () => {
    if (!plForm.finished_unit_id) { toast.error('Pick a finished unit first'); return; }
    try {
      const res = await api().get(`/picklists/suggest/${plForm.finished_unit_id}`);
      setPlForm(f => ({ ...f, items: [...f.items, ...res.data.suggestions] }));
      toast.success(`Added ${res.data.suggestions.length} suggested items`);
    } catch { toast.error('Failed to suggest'); }
  };

  // iter69 — pull picklist items directly from a released BOM (no finished unit required)
  const fillFromBom = async () => {
    if (!bomToAdd) { toast.error('Pick a BOM first'); return; }
    try {
      const r = await api().get(`/boms/${bomToAdd}`);
      const bom = r.data || {};
      const newItems = (bom.lines || []).map(ln => ({
        item_sku: ln.component_sku,
        item_name: ln.component_name || ln.component_sku,
        quantity: ln.quantity_per_unit || 1,
        uom: ln.uom || 'EA',
        location_label: ln.location_label || '',
        serial_number: '',
        source_bom: bom.bom_variant_code || bom.bom_number || bom.id,
      }));
      setPlForm(f => ({
        ...f,
        items: [...f.items, ...newItems],
        title: f.title || `${bom.bom_variant_code || bom.bom_number || 'BOM'} pick`,
      }));
      toast.success(`Added ${newItems.length} BOM component(s) to picklist`);
      setBomToAdd('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to pull BOM'); }
  };
  const addPlItem = () => setPlForm(f => ({ ...f, items: [...f.items, { item_name: '', quantity: 1, uom: 'EA' }] }));
  const removePlItem = (i) => setPlForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updatePlItem = (i, patch) => setPlForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const savePicklist = async () => {
    try {
      if (plEditing) {
        await api().put(`/picklists/${plEditing.id}`, plForm);
        toast.success('Picklist updated');
      } else {
        await api().post('/picklists', plForm);
        toast.success('Picklist created');
      }
      setPlDialogOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deletePicklist = async (id) => {
    if (!window.confirm('Delete this picklist?')) return;
    try { await api().delete(`/picklists/${id}`); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const printPicklist = async (pl) => {
    const { openBrandedPrintWindow, PrintHelpers: H } = await import('../utils/brandedPrint');
    const u = unitMap[pl.finished_unit_id];
    const cols = [
      { key: 'item_sku', label: 'SKU', mono: true },
      { key: 'item_name', label: 'Item' },
      { key: 'serial_number', label: 'Serial', mono: true },
      { key: 'quantity', label: 'Qty', mono: true },
      { key: 'uom', label: 'UoM' },
      { key: 'location_label', label: 'Location' },
      { key: 'picked', label: 'Picked', fmt: () => '<span style="display:inline-block; width:16pt; height:16pt; border:1px solid #475569;"></span>' },
    ];
    const qrUrl = `${FRONTEND_URL}/shipping?picklist=${encodeURIComponent(pl.picklist_number || pl.id)}`;
    const meta = H.kvGrid([
      ['Picklist', pl.picklist_number || '—'],
      ['Title', pl.title || '—'],
      ['Customer', pl.customer || '—'],
      ['Linked Unit', u?.serial_number || '—'],
      ['Prepared By', pl.prepared_by_name || '—'],
      ['Items', String((pl.items || []).length)],
    ]);
    await openBrandedPrintWindow({
      title: 'PICKLIST',
      subtitle: pl.picklist_number || '',
      orientation: 'portrait',
      qrUrl,
      body: `${meta}${H.sectionTitle('Items to Pick')}${H.table(cols, pl.items || [])}${pl.notes ? `${H.sectionTitle('Notes')}${H.callout(H.escape(pl.notes))}` : ''}${H.signoffLines('Picker Signature', 'Date')}`,
      footerNote: 'Shipping · Pick & Pack',
    });
  };

  // ===== BoL handlers =====
  const openBolCreate = (fromUnit) => {
    setBolEditing(null);
    setBolForm({ ...blankBol(), finished_unit_id: fromUnit?.id || '' });
    setBolDialogOpen(true);
  };
  const openBolEdit = (bol) => {
    setBolEditing(bol);
    setBolForm({
      finished_unit_id: bol.finished_unit_id || '',
      picklist_id: bol.picklist_id || '',
      shipper_name: bol.shipper_name || '',
      shipper_address: bol.shipper_address || '',
      consignee_name: bol.consignee_name || '',
      consignee_address: bol.consignee_address || '',
      carrier: bol.carrier || '',
      pro_number: bol.pro_number || '',
      freight_terms: bol.freight_terms || 'Prepaid',
      items: bol.items || [],
      special_instructions: bol.special_instructions || '',
    });
    setBolDialogOpen(true);
  };
  const fillBolFromUnit = () => {
    if (!bolForm.finished_unit_id) { toast.error('Pick a finished unit first'); return; }
    const u = unitMap[bolForm.finished_unit_id];
    if (!u) { toast.error('Unit not found'); return; }
    const items = [{
      description: `Finished Unit · serial ${u.serial_number}`,
      serial_number: u.serial_number,
      quantity: 1,
    }];
    setBolForm(f => ({
      ...f,
      consignee_name: u.shipped_to || f.consignee_name,
      items: [...f.items, ...items],
    }));
    toast.success('Pulled defaults from unit');
  };
  const addBolItem = () => setBolForm(f => ({ ...f, items: [...f.items, { description: '', quantity: 1 }] }));
  const removeBolItem = (i) => setBolForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateBolItem = (i, patch) => setBolForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));

  const saveBol = async (issueAfter) => {
    try {
      let id;
      if (bolEditing) {
        await api().put(`/bills-of-lading/${bolEditing.id}`, bolForm);
        id = bolEditing.id;
        toast.success('BOL updated');
      } else {
        const res = await api().post('/bills-of-lading', bolForm);
        id = res.data.id;
        toast.success('BOL draft created');
      }
      if (issueAfter) {
        const r = await api().post(`/bills-of-lading/${id}/issue`);
        toast.success(`Issued: ${r.data.bol_number}`);
      }
      setBolDialogOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const issueBol = async (bol) => {
    if (!window.confirm('Issue this BOL? It becomes immutable and gets a sequential number.')) return;
    try {
      const r = await api().post(`/bills-of-lading/${bol.id}/issue`);
      toast.success(`Issued ${r.data.bol_number}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const voidBol = async (bol) => {
    const reason = window.prompt('Void reason?');
    if (!reason) return;
    try { await api().post(`/bills-of-lading/${bol.id}/void`, { reason }); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteBol = async (id) => {
    if (!window.confirm('Delete this draft BOL?')) return;
    try { await api().delete(`/bills-of-lading/${id}`); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const printBol = async (bol) => {
    const { openBrandedPrintWindow, PrintHelpers: H } = await import('../utils/brandedPrint');
    const u = unitMap[bol.finished_unit_id];
    const linkedPl = bol.picklist_id ? picklists.find(p => p.id === bol.picklist_id) : null;
    const cols = [
      { key: 'description', label: 'Description' },
      { key: 'serial_number', label: 'Serial', mono: true },
      { key: 'quantity', label: 'Qty', mono: true },
      { key: 'weight', label: 'Weight', mono: true, fmt: (v, r) => v != null ? `${v} ${r.weight_uom || 'lb'}` : '—' },
    ];
    // iter68 — BOL QR code now links to the Finished Unit public report (work order context).
    // Linked Picklist + Work Order QR rendered inline in the body for matching/scanning.
    const unitQr = u?.public_token
      ? `${FRONTEND_URL}/public/finished-units/${u.public_token}`
      : (u ? `${FRONTEND_URL}/finished-units?serial=${encodeURIComponent(u.serial_number || '')}` : '');
    const qrPanel = `<div class="grid-3" style="margin-top:8pt;">${[
      ['Finished Unit', u?.serial_number, unitQr],
      ['Pick List', linkedPl?.picklist_number, linkedPl ? `${FRONTEND_URL}/shipping?picklist=${encodeURIComponent(linkedPl.picklist_number || linkedPl.id)}` : ''],
      ['Work Order', u?.work_order_number || u?.linked_work_order_number, u?.linked_work_order_id ? `${FRONTEND_URL}/work-orders?id=${u.linked_work_order_id}` : ''],
    ].map(([label, num, url]) => `
      <div style="border:1px solid #cbd5e1; padding:6pt; border-radius:4px; text-align:center;">
        <div class="label" style="text-align:center;">${H.escape(label)}</div>
        <div class="mono" style="font-size:9pt; margin-bottom:4pt;">${H.escape(num || '—')}</div>
        ${url ? `<img src="https://api.qrserver.com/v1/create-qr-code/?size=90x90&data=${encodeURIComponent(url)}" width="90" height="90" alt="QR" />` : '<div style="height:90px;line-height:90px;color:#94a3b8;font-size:9pt;">No QR</div>'}
      </div>
    `).join('')}</div>`;

    const statusColor = bol.status === 'Issued' ? '#16a34a' : bol.status === 'Voided' ? '#dc2626' : '#6b7280';
    const headerMeta = `<div class="grid-2">
      <div style="border:1px solid #cbd5e1; padding:10pt; border-radius:4px;"><div class="label">Shipper</div><div class="value"><strong>${H.escape(bol.shipper_name)}</strong></div><div style="white-space:pre-line; font-size:10pt; color:#374151;">${H.escape(bol.shipper_address)}</div></div>
      <div style="border:1px solid #cbd5e1; padding:10pt; border-radius:4px;"><div class="label">Consignee</div><div class="value"><strong>${H.escape(bol.consignee_name)}</strong></div><div style="white-space:pre-line; font-size:10pt; color:#374151;">${H.escape(bol.consignee_address)}</div></div>
    </div>`;
    const carrierMeta = H.kvGrid([
      ['Carrier', bol.carrier || '—'],
      ['PRO #', bol.pro_number || '—'],
      ['Freight Terms', bol.freight_terms || '—'],
      ['Issued', bol.issued_at ? `${new Date(bol.issued_at).toLocaleString()} by ${bol.issued_by_name || ''}` : '—'],
    ]);

    await openBrandedPrintWindow({
      title: 'BILL OF LADING',
      subtitle: bol.bol_number || '(DRAFT)',
      orientation: 'portrait',
      statusBadge: H.statusBadge(bol.status || 'Draft', statusColor),
      body: `${bol.status === 'Draft' ? H.callout('<strong style="color:#d97706;">DRAFT</strong> — not yet issued. Number will be minted on Issue.') : ''}
        ${bol.status === 'Voided' ? H.callout(`<strong style="color:#dc2626;">VOID</strong> — ${H.escape(bol.void_reason || '')}`) : ''}
        ${headerMeta}
        ${carrierMeta}
        ${H.sectionTitle('Cross-References (scan to verify)')}
        ${qrPanel}
        ${H.sectionTitle('Goods')}
        ${H.table(cols, bol.items || [])}
        ${bol.special_instructions ? `${H.sectionTitle('Special Instructions')}${H.callout(H.escape(bol.special_instructions))}` : ''}
        <div class="signoff" style="grid-template-columns: 1fr 1fr 1fr; gap: 24pt;">
          <div class="line">Shipper Signature / Date</div>
          <div class="line">Carrier Signature / Date</div>
          <div class="line">Consignee Signature / Date</div>
        </div>`,
      footerNote: bol.bol_number ? `BOL ${bol.bol_number} · Controlled Shipping Document` : 'Controlled Shipping Document',
      qrUrl: unitQr || undefined,
    });
  };

  const openView = (kind, doc) => { setViewKind(kind); setViewDoc(doc); setViewOpen(true); };

  const filteredPls = picklists.filter(p => !search || (p.picklist_number || '').toLowerCase().includes(search.toLowerCase()) || (p.customer || '').toLowerCase().includes(search.toLowerCase()) || (p.title || '').toLowerCase().includes(search.toLowerCase()));
  const filteredBols = bols.filter(b => !search || (b.bol_number || '').toLowerCase().includes(search.toLowerCase()) || (b.consignee_name || '').toLowerCase().includes(search.toLowerCase()) || (b.carrier || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4 animate-slideIn" data-testid="shipping-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Shipping</h1>
          <p className="text-slate-500 text-sm">Picklists and Bills of Lading. Sequential BOL numbers (BOL-000001) tied back to the finished unit.</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by number / customer / carrier…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="shipping-search" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="picklists" data-testid="tab-picklists"><ClipboardList className="w-4 h-4 mr-2" /> Picklists ({search ? `${filteredPls.length} / ${picklists.length}` : picklists.length})</TabsTrigger>
          <TabsTrigger value="bols" data-testid="tab-bols"><Receipt className="w-4 h-4 mr-2" /> Bills of Lading ({search ? `${filteredBols.length} / ${bols.length}` : bols.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="picklists" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button onClick={openPlCreate} className="bg-orange-500 hover:bg-orange-600" data-testid="picklist-create-btn">
              <Plus className="w-4 h-4 mr-1" /> New Picklist
            </Button>
          </div>
          <Card className="industrial-card">
            <CardContent className="p-0">
              {loading ? <Loader /> : filteredPls.length === 0 ? <Empty kind="picklist" onCreate={openPlCreate} /> : (
                <Table className="data-table">
                  <TableHeader><TableRow>
                    <TableHead>Picklist #</TableHead><TableHead>Title</TableHead><TableHead>Customer</TableHead><TableHead>Unit</TableHead><TableHead>Items</TableHead><TableHead>Prepared</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {filteredPls.map(pl => {
                      const u = pl.finished_unit_id ? unitMap[pl.finished_unit_id] : null;
                      return (
                        <TableRow key={pl.id} data-testid={`picklist-row-${pl.id}`}>
                          <TableCell className="font-mono font-bold">{pl.picklist_number}</TableCell>
                          <TableCell>{pl.title || '—'}</TableCell>
                          <TableCell>{pl.customer || '—'}</TableCell>
                          <TableCell className="font-mono text-xs">{u?.serial_number || '—'}</TableCell>
                          <TableCell><Badge variant="outline">{pl.items?.length || 0}</Badge></TableCell>
                          <TableCell className="text-xs">{pl.prepared_by_name || '—'}</TableCell>
                          <TableCell className="text-xs">{pl.created_at ? new Date(pl.created_at).toLocaleDateString() : '—'}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => openView('pl', pl)} data-testid={`picklist-view-${pl.id}`}><Eye className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => printPicklist(pl)} data-testid={`picklist-print-${pl.id}`}><Printer className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => openPlEdit(pl)}><FileText className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => deletePicklist(pl.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bols" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button onClick={() => openBolCreate(null)} className="bg-blue-600 hover:bg-blue-700" data-testid="bol-create-btn">
              <Plus className="w-4 h-4 mr-1" /> New BOL
            </Button>
          </div>
          <Card className="industrial-card">
            <CardContent className="p-0">
              {loading ? <Loader /> : filteredBols.length === 0 ? <Empty kind="bol" onCreate={() => openBolCreate(null)} /> : (
                <Table className="data-table">
                  <TableHeader><TableRow>
                    <TableHead>BOL #</TableHead><TableHead>Status</TableHead><TableHead>Consignee</TableHead><TableHead>Carrier</TableHead><TableHead>Unit</TableHead><TableHead>Items</TableHead><TableHead>Issued</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {filteredBols.map(b => {
                      const u = b.finished_unit_id ? unitMap[b.finished_unit_id] : null;
                      return (
                        <TableRow key={b.id} data-testid={`bol-row-${b.id}`}>
                          <TableCell className="font-mono font-bold">{b.bol_number || <span className="text-slate-400 italic">draft</span>}</TableCell>
                          <TableCell><Badge className={b.status === 'Issued' ? 'bg-emerald-600' : b.status === 'Voided' ? 'bg-red-600' : 'bg-slate-500'}>{b.status}</Badge></TableCell>
                          <TableCell>{b.consignee_name || '—'}</TableCell>
                          <TableCell>{b.carrier || '—'}</TableCell>
                          <TableCell className="font-mono text-xs">{u?.serial_number || '—'}</TableCell>
                          <TableCell><Badge variant="outline">{b.items?.length || 0}</Badge></TableCell>
                          <TableCell className="text-xs">{b.issued_at ? new Date(b.issued_at).toLocaleDateString() : '—'}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => openView('bol', b)} data-testid={`bol-view-${b.id}`}><Eye className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => printBol(b)} data-testid={`bol-print-${b.id}`}><Printer className="w-4 h-4" /></Button>
                            {b.status === 'Draft' && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => openBolEdit(b)}><FileText className="w-4 h-4" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => issueBol(b)} className="text-emerald-600" data-testid={`bol-issue-${b.id}`} title="Issue (mint number)"><Lock className="w-4 h-4" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => deleteBol(b.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                              </>
                            )}
                            {b.status === 'Issued' && (
                              <Button size="sm" variant="ghost" onClick={() => voidBol(b)} className="text-red-600" title="Void (admin)" data-testid={`bol-void-${b.id}`}><X className="w-4 h-4" /></Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ Picklist Dialog ============ */}
      <Dialog open={plDialogOpen} onOpenChange={setPlDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{plEditing ? `Edit Picklist ${plEditing.picklist_number}` : 'New Picklist'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Finished Unit</Label>
                <Select value={plForm.finished_unit_id || 'none'} onValueChange={v => setPlForm({ ...plForm, finished_unit_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="picklist-unit-select"><SelectValue placeholder="Select finished unit (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No unit</SelectItem>
                    {units.slice(0, 60).map(u => <SelectItem key={u.id} value={u.id}>{u.serial_number} · {u.unit_kind === 'final_product' ? 'Final' : 'Part'}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Customer</Label>
                <Input value={plForm.customer} onChange={e => setPlForm({ ...plForm, customer: e.target.value })} placeholder="Customer name" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Title</Label>
                <Input value={plForm.title} onChange={e => setPlForm({ ...plForm, title: e.target.value })} placeholder="e.g., Order #1234 — outbound" />
              </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-base">Items ({plForm.items.length})</Label>
              <div className="flex gap-2 flex-wrap items-center">
                <Select value={bomToAdd || 'none'} onValueChange={(v) => setBomToAdd(v === 'none' ? '' : v)}>
                  <SelectTrigger className="h-8 w-56" data-testid="picklist-bom-select"><SelectValue placeholder="Pick a BOM to add…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Pick a BOM —</SelectItem>
                    {boms.slice(0, 100).map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.bom_variant_code || b.bom_number} {b.bom_revision ? `(Rev ${b.bom_revision})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={fillFromBom} disabled={!bomToAdd} data-testid="picklist-add-bom-btn"><Plus className="w-4 h-4 mr-1" /> Add BOM</Button>
                <Button size="sm" variant="outline" onClick={fillFromUnit} disabled={!plForm.finished_unit_id} data-testid="picklist-suggest-btn">Suggest from Unit</Button>
                <Button size="sm" variant="outline" onClick={addPlItem} data-testid="picklist-add-item-btn"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
              </div>
            </div>
            {plForm.items.length === 0 && <p className="text-xs text-slate-400 border border-dashed rounded-sm p-3 text-center">No items yet. Click "Suggest from Unit" or "Add Item".</p>}
            {plForm.items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 border border-slate-200 rounded-sm p-2 items-end" data-testid={`picklist-item-${idx}`}>
                <div className="col-span-3"><Label className="text-xs">SKU</Label><Input value={it.item_sku || ''} onChange={e => updatePlItem(idx, { item_sku: e.target.value })} /></div>
                <div className="col-span-3"><Label className="text-xs">Item</Label><Input value={it.item_name || ''} onChange={e => updatePlItem(idx, { item_name: e.target.value })} /></div>
                <div className="col-span-2"><Label className="text-xs">Serial</Label><Input value={it.serial_number || ''} onChange={e => updatePlItem(idx, { serial_number: e.target.value })} /></div>
                <div className="col-span-1"><Label className="text-xs">Qty</Label><Input type="number" value={it.quantity || 1} onChange={e => updatePlItem(idx, { quantity: parseFloat(e.target.value) || 0 })} /></div>
                <div className="col-span-1"><Label className="text-xs">UoM</Label><Input value={it.uom || 'EA'} onChange={e => updatePlItem(idx, { uom: e.target.value })} /></div>
                <div className="col-span-1"><Label className="text-xs">Loc</Label><Input value={it.location_label || ''} onChange={e => updatePlItem(idx, { location_label: e.target.value })} /></div>
                <Button size="sm" variant="ghost" onClick={() => removePlItem(idx)} className="text-red-600 col-span-1"><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea value={plForm.notes} onChange={e => setPlForm({ ...plForm, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlDialogOpen(false)}>Cancel</Button>
            <Button onClick={savePicklist} className="bg-orange-500 hover:bg-orange-600" data-testid="picklist-save-btn">Save Picklist</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ BoL Dialog ============ */}
      <Dialog open={bolDialogOpen} onOpenChange={setBolDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{bolEditing ? `Edit BOL ${bolEditing.bol_number || '(draft)'}` : 'New Bill of Lading'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Finished Unit</Label>
                <Select value={bolForm.finished_unit_id || 'none'} onValueChange={v => setBolForm({ ...bolForm, finished_unit_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="bol-unit-select"><SelectValue placeholder="Select finished unit" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No unit</SelectItem>
                    {units.slice(0, 60).map(u => <SelectItem key={u.id} value={u.id}>{u.serial_number} · {u.unit_kind === 'final_product' ? 'Final' : 'Part'}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Linked Picklist (optional)</Label>
                <Select value={bolForm.picklist_id || 'none'} onValueChange={v => setBolForm({ ...bolForm, picklist_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {picklists.map(pl => <SelectItem key={pl.id} value={pl.id}>{pl.picklist_number} {pl.customer ? `· ${pl.customer}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Shipper Name</Label><Input value={bolForm.shipper_name} onChange={e => setBolForm({ ...bolForm, shipper_name: e.target.value })} /></div>
              <div className="space-y-1"><Label>Consignee Name</Label><Input value={bolForm.consignee_name} onChange={e => setBolForm({ ...bolForm, consignee_name: e.target.value })} /></div>
              <div className="space-y-1"><Label>Shipper Address</Label><Textarea rows={2} value={bolForm.shipper_address} onChange={e => setBolForm({ ...bolForm, shipper_address: e.target.value })} /></div>
              <div className="space-y-1"><Label>Consignee Address</Label><Textarea rows={2} value={bolForm.consignee_address} onChange={e => setBolForm({ ...bolForm, consignee_address: e.target.value })} /></div>
              <div className="space-y-1"><Label>Carrier</Label><Input value={bolForm.carrier} onChange={e => setBolForm({ ...bolForm, carrier: e.target.value })} /></div>
              <div className="space-y-1"><Label>PRO #</Label><Input value={bolForm.pro_number} onChange={e => setBolForm({ ...bolForm, pro_number: e.target.value })} /></div>
              <div className="space-y-1">
                <Label>Freight Terms</Label>
                <Select value={bolForm.freight_terms} onValueChange={v => setBolForm({ ...bolForm, freight_terms: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Prepaid">Prepaid</SelectItem>
                    <SelectItem value="Collect">Collect</SelectItem>
                    <SelectItem value="Third Party">Third Party</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="outline" size="sm" onClick={fillBolFromUnit} disabled={!bolForm.finished_unit_id} data-testid="bol-fill-from-unit">Pull from Unit</Button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-base">Items ({bolForm.items.length})</Label>
              <Button size="sm" variant="outline" onClick={addBolItem} data-testid="bol-add-item-btn"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
            </div>
            {bolForm.items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 border border-slate-200 rounded-sm p-2 items-end" data-testid={`bol-item-${idx}`}>
                <div className="col-span-5"><Label className="text-xs">Description</Label><Input value={it.description || ''} onChange={e => updateBolItem(idx, { description: e.target.value })} /></div>
                <div className="col-span-3"><Label className="text-xs">Serial</Label><Input value={it.serial_number || ''} onChange={e => updateBolItem(idx, { serial_number: e.target.value })} /></div>
                <div className="col-span-1"><Label className="text-xs">Qty</Label><Input type="number" value={it.quantity || 1} onChange={e => updateBolItem(idx, { quantity: parseFloat(e.target.value) || 0 })} /></div>
                <div className="col-span-1"><Label className="text-xs">Wt</Label><Input type="number" value={it.weight ?? ''} onChange={e => updateBolItem(idx, { weight: e.target.value === '' ? null : parseFloat(e.target.value) })} /></div>
                <div className="col-span-1"><Label className="text-xs">UoM</Label><Input value={it.weight_uom || 'lb'} onChange={e => updateBolItem(idx, { weight_uom: e.target.value })} /></div>
                <Button size="sm" variant="ghost" onClick={() => removeBolItem(idx)} className="text-red-600 col-span-1"><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <div className="space-y-1"><Label>Special Instructions</Label><Textarea rows={2} value={bolForm.special_instructions} onChange={e => setBolForm({ ...bolForm, special_instructions: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBolDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveBol(false)} data-testid="bol-save-draft-btn">Save Draft</Button>
            <Button onClick={() => saveBol(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bol-save-and-issue-btn">
              <Lock className="w-4 h-4 mr-1" /> Save &amp; Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ View Dialog ============ */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{viewKind === 'pl' ? `Picklist ${viewDoc?.picklist_number}` : `BOL ${viewDoc?.bol_number || '(draft)'}`}</DialogTitle></DialogHeader>
          {viewDoc && viewKind === 'pl' && <PicklistView pl={viewDoc} unit={unitMap[viewDoc.finished_unit_id]} />}
          {viewDoc && viewKind === 'bol' && <BolView bol={viewDoc} unit={unitMap[viewDoc.finished_unit_id]} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewOpen(false)}>Close</Button>
            <Button onClick={() => viewKind === 'pl' ? printPicklist(viewDoc) : printBol(viewDoc)} className="bg-orange-500 hover:bg-orange-600">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Loader = () => <div className="h-32 flex items-center justify-center"><div className="spinner" /></div>;
const Empty = ({ kind, onCreate }) => (
  <div className="h-40 flex flex-col items-center justify-center text-slate-500 gap-1">
    {kind === 'picklist' ? <ClipboardList className="w-10 h-10 text-slate-300" /> : <Receipt className="w-10 h-10 text-slate-300" />}
    <p>No {kind === 'picklist' ? 'picklists' : 'bills of lading'} yet</p>
    <Button variant="link" onClick={onCreate} className="text-orange-600">Create the first one</Button>
  </div>
);

const PicklistView = ({ pl, unit }) => (
  <div className="space-y-2 text-sm">
    <div className="grid grid-cols-2 gap-2">
      <div><p className="text-xs text-slate-500">Number</p><p className="font-mono font-bold">{pl.picklist_number}</p></div>
      <div><p className="text-xs text-slate-500">Title</p><p>{pl.title || '—'}</p></div>
      <div><p className="text-xs text-slate-500">Customer</p><p>{pl.customer || '—'}</p></div>
      <div><p className="text-xs text-slate-500">Unit</p><p className="font-mono">{unit?.serial_number || '—'}</p></div>
      <div><p className="text-xs text-slate-500">Prepared By</p><p>{pl.prepared_by_name || '—'}</p></div>
      <div><p className="text-xs text-slate-500">Created</p><p>{pl.created_at ? new Date(pl.created_at).toLocaleString() : '—'}</p></div>
    </div>
    <Table className="data-table">
      <TableHeader><TableRow>
        <TableHead>SKU</TableHead><TableHead>Item</TableHead><TableHead>Serial</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>UoM</TableHead><TableHead>Loc</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {(pl.items || []).map((it, i) => (
          <TableRow key={i}>
            <TableCell className="font-mono text-xs">{it.item_sku || '—'}</TableCell>
            <TableCell className="text-xs">{it.item_name || '—'}</TableCell>
            <TableCell className="font-mono text-xs">{it.serial_number || '—'}</TableCell>
            <TableCell className="text-right text-xs">{it.quantity}</TableCell>
            <TableCell className="text-xs">{it.uom}</TableCell>
            <TableCell className="text-xs">{it.location_label || '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {pl.notes && <p className="text-xs text-slate-500"><strong>Notes:</strong> {pl.notes}</p>}
  </div>
);

const BolView = ({ bol, unit }) => (
  <div className="space-y-2 text-sm">
    <div className="grid grid-cols-2 gap-2">
      <div><p className="text-xs text-slate-500">BOL #</p><p className="font-mono font-bold">{bol.bol_number || 'draft'}</p></div>
      <div><p className="text-xs text-slate-500">Status</p><Badge className={bol.status === 'Issued' ? 'bg-emerald-600' : bol.status === 'Voided' ? 'bg-red-600' : 'bg-slate-500'}>{bol.status}</Badge></div>
      <div><p className="text-xs text-slate-500">Shipper</p><p>{bol.shipper_name || '—'}</p><p className="text-xs whitespace-pre-line">{bol.shipper_address || ''}</p></div>
      <div><p className="text-xs text-slate-500">Consignee</p><p>{bol.consignee_name || '—'}</p><p className="text-xs whitespace-pre-line">{bol.consignee_address || ''}</p></div>
      <div><p className="text-xs text-slate-500">Carrier</p><p>{bol.carrier || '—'}</p></div>
      <div><p className="text-xs text-slate-500">PRO / Terms</p><p>{bol.pro_number || '—'} · {bol.freight_terms}</p></div>
      <div><p className="text-xs text-slate-500">Linked Unit</p><p className="font-mono">{unit?.serial_number || '—'}</p></div>
      <div><p className="text-xs text-slate-500">Issued</p><p className="text-xs">{bol.issued_at ? `${new Date(bol.issued_at).toLocaleString()} by ${bol.issued_by_name}` : '—'}</p></div>
    </div>
    <Table className="data-table">
      <TableHeader><TableRow><TableHead>Description</TableHead><TableHead>Serial</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Weight</TableHead></TableRow></TableHeader>
      <TableBody>
        {(bol.items || []).map((it, i) => (
          <TableRow key={i}>
            <TableCell className="text-xs">{it.description}</TableCell>
            <TableCell className="font-mono text-xs">{it.serial_number || '—'}</TableCell>
            <TableCell className="text-right text-xs">{it.quantity}</TableCell>
            <TableCell className="text-right text-xs">{it.weight ?? '—'} {it.weight_uom || ''}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {bol.special_instructions && <p className="text-xs"><strong>Special Instructions:</strong> {bol.special_instructions}</p>}
    {bol.status === 'Voided' && <p className="text-xs text-red-600"><strong>VOID:</strong> {bol.void_reason || '—'}</p>}
  </div>
);

// ===== Defaults =====
const blankPicklist = () => ({ finished_unit_id: '', title: '', customer: '', items: [], notes: '' });
const blankBol = () => ({
  finished_unit_id: '', picklist_id: '', shipper_name: '', shipper_address: '',
  consignee_name: '', consignee_address: '', carrier: '', pro_number: '',
  freight_terms: 'Prepaid', items: [], special_instructions: '',
});

export default ShippingPage;
